/* eslint-disable no-console */
import { useNotification } from '@agilelab/plugin-wb-notification';
import {
  customFrontendApiRef,
  CustomMicrofrontendOptions,
  MaintenancePage,
  useEnabledFeatures,
  useHomeUrl,
  usePermissions,
  shouldShowTabByCommercialModules,
  CustomViewPages,
  categoryToCommercial,
  WbSidebar,
  WbSidebarAvatar,
  WbSidebarDivider,
  WbSidebarGroup,
  WbSidebarItem,
  WbSidebarNotificationsItem,
  WbSidebarPage,
  WbSidebarScrollWrapper,
  WbSidebarSpace,
  WbSidebarSubmenu,
  WbSidebarSubmenuItem,
  SubmenuModeEnum,
  WbSidebarSubmenuAvatar,
  AboutModalButton,
  WbLogoutButton,
  MarketplaceCustomPagesEnum,
  useMarketplaceCustomPagesConfig,
  useDeveloperToolsEnabled,
} from '@agilelab/plugin-wb-platform';
import { getSupervisionRequiredModules } from '@agilelab/plugin-wb-platform-settings';
import {
  getDynamicConfigRequiredModules,
  WitboostCommercialModule,
} from '@agilelab/plugin-wb-platform-common';
import { practiceShaperApiRef } from '@agilelab/plugin-wb-practice-shaper';
import {
  catalogEntityReadPermission,
  rbacRoleCreatePermission,
  rbacRoleDeletePermission,
  rbacRoleEditPermission,
  systemPrototypeEditPermission,
  workflowEngineDefinitionViewPermission,
} from '@agilelab/plugin-wb-rbac-common';
import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import { configApiRef } from '@mainstage/core-plugin-api';
import AccountTreeOutlinedIcon from '@material-ui/icons/AccountTreeOutlined';
import BallotIcon from '@material-ui/icons/Ballot';
import HomeIcon from '@material-ui/icons/Home';
import ListAltIcon from '@material-ui/icons/ListAlt';
import NotificationsIcon from '@material-ui/icons/Notifications';
import SettingsIcon from '@material-ui/icons/Settings';
import { useQuery } from '@tanstack/react-query';
import { PropsWithChildren, useMemo } from 'react';
import {
  BlueprintIcon,
  CreateIcon,
  GlossaryIcon,
  GovernanceRegistryIcon,
  GovernanceSearchResultsIcon,
  ManageSearchIcon,
  MyDataProductIcon,
  PoliciesOverviewIcon,
} from '@agilelab/plugin-wb-platform';

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const {
    loading: enabledFeaturesLoading,
    data: features,
    isCommercialModuleLicensed,
  } = useEnabledFeatures();
  const { enabled: developerToolsEnabled } = useDeveloperToolsEnabled();
  const practiceShaperApi = useApi(practiceShaperApiRef);
  const customFrontendApi = useApi(customFrontendApiRef);
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);

  const adminMenusEnabled =
    configApi.getOptionalBoolean(
      'mesh.platformSettings.ui.experimental.adminMenusEnabled',
    ) ?? true;

  const { anyAllowed: canAccessRoleAndPermissionsPage } = usePermissions([
    {
      permission: rbacRoleCreatePermission,
    },
    {
      permission: rbacRoleEditPermission,
    },
    {
      permission: rbacRoleDeletePermission,
    },
  ]);

  const { allAllowed: canAccessPrototypePage } = usePermissions([
    {
      permission: systemPrototypeEditPermission,
    },
  ]);

  const { allAllowed: canAccessCatalogPage } = usePermissions([
    {
      permission: catalogEntityReadPermission,
    },
  ]);

  const { allAllowed: canAccessWorkflowsPage } = usePermissions([
    {
      permission: workflowEngineDefinitionViewPermission,
    },
  ]);

  const { isGuestUser } = useHomeUrl();

  const { data: isMaintenanceActive } = useQuery({
    queryKey: ['maintenanceActive'],
    queryFn: async () => {
      return practiceShaperApi.isMaintenanceActive(
        await identityApi.getCredentials(),
      );
    },
  });

  const { unreadNotificationCount } = useNotification();
  const customPagesConfiguration = useMarketplaceCustomPagesConfig(
    MarketplaceCustomPagesEnum.Page,
  );

  const customPages: CustomMicrofrontendOptions[] = useMemo(() => {
    if (!customPagesConfiguration.data) return [];
    const getCustomFrontends = customFrontendApi.makeCustomMicrofrontends(
      customPagesConfiguration.data,
    );
    return getCustomFrontends();
  }, [customPagesConfiguration.data, customFrontendApi]);

  if (enabledFeaturesLoading) {
    return <></>;
  }

  if (isMaintenanceActive) {
    return <MaintenancePage />;
  }

  // Determine Supervision tab visibility based on inner tabs' required modules
  const supervisionTabVisible = (() => {
    const requiredCommercialModules = getSupervisionRequiredModules(features);
    if (requiredCommercialModules.length === 0) return false;
    return shouldShowTabByCommercialModules(
      requiredCommercialModules,
      // reuse context predicate for commercial modules
      isCommercialModuleLicensed,
    );
  })();

  const configTabVisible = shouldShowTabByCommercialModules(
    getDynamicConfigRequiredModules(),
    isCommercialModuleLicensed,
  );

  const controlPlaneLicensed = shouldShowTabByCommercialModules(
    [WitboostCommercialModule.ControlPlane],
    isCommercialModuleLicensed,
  );

  const dynamicDataProvidersVisible = adminMenusEnabled && controlPlaneLicensed;

  const templatesSectionLicensed = shouldShowTabByCommercialModules(
    [
      WitboostCommercialModule.ControlPlane,
      WitboostCommercialModule.MarketPlane,
    ],
    isCommercialModuleLicensed,
  );

  const templatesNavVisible = templatesSectionLicensed && canAccessCatalogPage;

  // Determine if there are any Custom Views available for the current license
  const customViewsAvailable = (() => {
    return CustomViewPages.getAll().some(id => {
      const meta = CustomViewPages.getMetadata(id);
      if (!meta) return false;
      return isCommercialModuleLicensed(categoryToCommercial[meta.category]);
    });
  })();

  const getDefaultSettingPage = () => {
    // Determine first visible tab in the same order as the submenu
    const candidates: { visible: boolean; path: string }[] = [
      {
        visible: supervisionTabVisible,
        path: '/platform-settings/supervision',
      },
      {
        visible: features.practiceShaperEnabled,
        path: '/platform-settings/practice-shaper',
      },
      {
        visible: canAccessRoleAndPermissionsPage,
        path: '/platform-settings/rbac/roles-permissions',
      },
      {
        visible:
          adminMenusEnabled &&
          features.customViewEditEnabled &&
          customViewsAvailable,
        path: '/platform-settings/custom-view',
      },
      {
        visible: adminMenusEnabled && features.extensionManagerSectionEnabled,
        path: '/platform-settings/extension-manager',
      },
      {
        visible: developerToolsEnabled,
        path: '/platform-settings/developer-tools',
      },
      {
        visible: adminMenusEnabled && configTabVisible,
        path: '/platform-settings/dynamic-config',
      },
      {
        visible: dynamicDataProvidersVisible,
        path: '/platform-settings/descriptor-enrichment-providers',
      },
      // Events Download is always visible in submenu; include it before Catalog
      {
        visible: adminMenusEnabled,
        path: '/platform-settings/events-download',
      },
      {
        visible: features.softwareCatalogEnabled && canAccessCatalogPage,
        path: '/platform-settings/catalog',
      },
      {
        visible: templatesNavVisible,
        path: '/platform-settings/templates',
      },
      {
        // Users & Groups shows when user has catalog read permission
        visible: canAccessCatalogPage,
        path: '/platform-settings/iam',
      },
    ];

    const first = candidates.find(c => c.visible);
    return first ? first.path : '/platform-settings/events-download';
  };

  // Show Administration only if platform settings are enabled and at least one inner tab is visible
  const administrationVisible =
    features.platformSettingsEnabled &&
    (supervisionTabVisible ||
      features.practiceShaperEnabled ||
      canAccessRoleAndPermissionsPage ||
      (adminMenusEnabled &&
        features.customViewEditEnabled &&
        customViewsAvailable) ||
      (adminMenusEnabled && features.extensionManagerSectionEnabled) ||
      developerToolsEnabled ||
      (adminMenusEnabled && configTabVisible) ||
      dynamicDataProvidersVisible ||
      // Allow Users & Groups for all modules when user has catalog read permission
      canAccessCatalogPage ||
      (adminMenusEnabled && canAccessWorkflowsPage));

  return (
    <>
      <WbSidebarPage>
        <WbSidebar>
          <WbSidebarScrollWrapper>
            {features.marketplaceEnabled && !isGuestUser && (
              <WbSidebarGroup text="Data Marketplace" invisibleDivider>
                {features.marketplaceCatalogEnabled && (
                  <WbSidebarItem
                    icon={HomeIcon}
                    to="marketplace/home"
                    text="Home"
                  />
                )}
                {(features.marketplaceCatalogEnabled ||
                  features.marketplaceGraphEnabled) && (
                  <WbSidebarItem
                    icon={ManageSearchIcon}
                    extraActivationPaths={['marketplace/consumable-interface/']}
                    to="marketplace/search"
                    text="Marketplace Discovery"
                  />
                )}
                {features.businessConceptMapEnabled && (
                  <WbSidebarItem
                    icon={GlossaryIcon}
                    to="marketplace/business-concepts-map"
                    text="Business Concept Map"
                  />
                )}
                {features.dataContractsOverviewPageEnabled && (
                  <WbSidebarItem
                    icon={AccountTreeOutlinedIcon}
                    text="Data Contracts"
                    to="marketplace/data-contracts"
                  />
                )}
                {customPages.map(page => (
                  <WbSidebarItem
                    key={page.title}
                    icon={BallotIcon}
                    text={page.title}
                    to={`marketplace${page.route}`}
                  />
                ))}
              </WbSidebarGroup>
            )}
            {features.builderEnabled && (
              <WbSidebarGroup
                text="Builder"
                invisibleDivider={!features.marketplaceEnabled}
              >
                {features.templatesEnabled && (
                  <WbSidebarItem
                    extraActivationPaths={[
                      'scaffolder',
                      'catalog/default/edittemplate',
                      'catalog/default/reverseprovisioningtemplate',
                    ]}
                    excludedActivationPaths={[
                      'blueprints',
                      'scaffolder/blueprints',
                    ]}
                    icon={CreateIcon}
                    to="templates"
                    text="Templates"
                  />
                )}
                {features.blueprintsEnabled && (
                  <WbSidebarItem
                    extraActivationPaths={[
                      'blueprints',
                      'scaffolder/blueprints',
                    ]}
                    icon={BlueprintIcon}
                    to="blueprints"
                    text="Blueprints"
                  />
                )}
                {features.catalogEnabled && (
                  <>
                    <WbSidebarItem
                      icon={MyDataProductIcon}
                      to="my-projects/catalog"
                      text="My Projects"
                      extraActivationPaths={['catalog']}
                      excludedActivationPaths={[
                        'catalog/default/edittemplate',
                        'catalog/default/reverseprovisioningtemplate',
                      ]}
                    />
                    {features.prototypePageEnabled &&
                      canAccessPrototypePage && (
                        <WbSidebarItem
                          icon={ListAltIcon}
                          to="prototypes"
                          text="Backlog"
                        />
                      )}
                  </>
                )}
              </WbSidebarGroup>
            )}
            {features.governanceEnabled && (
              <WbSidebarGroup
                text="Governance"
                invisibleDivider={
                  !features.marketplaceEnabled && !features.builderEnabled
                }
              >
                <WbSidebarItem
                  icon={GovernanceRegistryIcon}
                  to="/governance/registry"
                  text="Registry"
                />
                <WbSidebarItem
                  icon={PoliciesOverviewIcon}
                  to="/governance/policies-overview"
                  text="Policies Overview"
                />
                <WbSidebarItem
                  icon={GovernanceSearchResultsIcon}
                  to="/governance/search-results"
                  text="Search Results"
                />
              </WbSidebarGroup>
            )}
          </WbSidebarScrollWrapper>
          <WbSidebarSpace />
          {(features.notificationsMenuEnabled || administrationVisible) && (
            <WbSidebarDivider sideMargin />
          )}
          {features.notificationsMenuEnabled && (
            <WbSidebarNotificationsItem
              unreadNotifications={unreadNotificationCount}
              icon={NotificationsIcon}
              to="notifications"
              text="Notifications"
            />
          )}
          {administrationVisible && (
            <WbSidebarItem
              icon={SettingsIcon}
              to={getDefaultSettingPage()}
              text="Administration"
              submenumode={SubmenuModeEnum.overlay}
              expandedMenuName="Administration"
            >
              <WbSidebarSubmenu title="Administration">
                {features.moduleOverridePageEnabled && (
                  <WbSidebarSubmenuItem
                    to="/platform-settings/modules"
                    title="Modules Control"
                  />
                )}
                {supervisionTabVisible && (
                  <WbSidebarSubmenuItem
                    to="/platform-settings/supervision"
                    title="Supervision"
                  />
                )}
                {features.practiceShaperEnabled && (
                  <WbSidebarSubmenuItem
                    to="/platform-settings/practice-shaper"
                    title="Practice Shaper"
                  />
                )}
                {canAccessRoleAndPermissionsPage && (
                  <WbSidebarSubmenuItem
                    to="/platform-settings/rbac/roles-permissions"
                    title="Roles & Permissions"
                  />
                )}
                {adminMenusEnabled &&
                  features.customViewEditEnabled &&
                  customViewsAvailable && (
                    <WbSidebarSubmenuItem
                      to="/platform-settings/custom-view"
                      title="Custom Views"
                    />
                  )}
                {adminMenusEnabled &&
                  features.extensionManagerSectionEnabled && (
                    <WbSidebarSubmenuItem
                      to="/platform-settings/extension-manager"
                      title="Extension Manager"
                    />
                  )}
                {adminMenusEnabled && configTabVisible && (
                  <WbSidebarSubmenuItem
                    to="/platform-settings/dynamic-config"
                    title="Configuration"
                  />
                )}
                {adminMenusEnabled && canAccessWorkflowsPage && (
                  <WbSidebarSubmenuItem
                    to="/platform-settings/workflows"
                    title="Workflows"
                  />
                )}
                {dynamicDataProvidersVisible && (
                  <WbSidebarSubmenuItem
                    to="/platform-settings/descriptor-enrichment-providers"
                    title="Descriptor Enrichment Providers"
                  />
                )}
                {adminMenusEnabled && (
                  <WbSidebarSubmenuItem
                    to="/platform-settings/events-download"
                    title="Events Download"
                  />
                )}
                {features.softwareCatalogEnabled && canAccessCatalogPage && (
                  <WbSidebarSubmenuItem
                    to="/platform-settings/catalog"
                    title="Entities"
                    excludedActivationPaths={[
                      'platform-settings/catalog/default/group',
                      'platform-settings/catalog/default/user',
                      'platform-settings/catalog/default/template',
                      'platform-settings/catalog/default/blueprint',
                      'platform-settings/catalog/default/edittemplate',
                      'platform-settings/catalog/default/reverseprovisioningtemplate',
                      'platform-settings/catalog/default/systemprototypetemplate',
                      'platform-settings/catalog/default/accesscontrolrequesttemplate',
                    ]}
                  />
                )}
                {templatesNavVisible && (
                  <WbSidebarSubmenuItem
                    to="/platform-settings/templates"
                    title="Templates"
                    extraActivationPaths={[
                      'platform-settings/catalog/default/template',
                      'platform-settings/catalog/default/blueprint',
                      'platform-settings/catalog/default/edittemplate',
                      'platform-settings/catalog/default/reverseprovisioningtemplate',
                      'platform-settings/catalog/default/systemprototypetemplate',
                      'platform-settings/catalog/default/accesscontrolrequesttemplate',
                    ]}
                  />
                )}
                {canAccessCatalogPage && (
                  <WbSidebarSubmenuItem
                    to="/platform-settings/iam"
                    title="IAM"
                    extraActivationPaths={[
                      'platform-settings/catalog/default/group',
                      'platform-settings/catalog/default/user',
                    ]}
                  />
                )}
                {developerToolsEnabled && (
                  <WbSidebarSubmenuItem
                    to="/platform-settings/developer-tools"
                    title="Developer Tools"
                  />
                )}
                <WbSidebarSpace />
                <AboutModalButton />
                <WbSidebarDivider />
                <WbLogoutButton submenu />
                <WbSidebarDivider noUnderMargin />
                <WbSidebarSubmenuAvatar
                  to="settings"
                  // Prevent Administration submenu to be active when in Settings page since we want standard sidebar there
                  excludedActivationPaths={['settings']}
                />
              </WbSidebarSubmenu>
            </WbSidebarItem>
          )}
          <WbSidebarDivider />
          <WbLogoutButton />
          <WbSidebarDivider noUnderMargin />
          <WbSidebarAvatar to="settings" />
        </WbSidebar>
        {children}
      </WbSidebarPage>
    </>
  );
};
